import {
  Button,
  Container,
  EmptyState,
  IconButton,
  Table,
  useConfirmDialog,
  useModal,
} from "@fluidattacks/design";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddCriteriaModal } from "./add-criteria-modal";
import { usePriorityPoliciesActions } from "./hooks";
import type { IPolicyItem, IVulnerabilitiesPriority } from "./types";

import { useAuthz } from "hooks";
import { useTable } from "hooks/use-table";

const prefix = "organization.tabs.policies.priority.";

const VulnerabilitiesPriority = ({
  organizationId,
}: IVulnerabilitiesPriority): JSX.Element => {
  const { t } = useTranslation();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const { canMutate } = useAuthz();
  const [search, setSearch] = useState("");
  const modalProps = useModal("priority-modal");
  const allowUpdate = canMutate("update_organization_priority_policies");
  const { isOpen, open, close } = modalProps;
  const { criteria, loading, handleDeletion, handleSubmit } =
    usePriorityPoliciesActions(close, confirm, organizationId);

  const tableData = useMemo((): IPolicyItem[] => {
    return criteria
      .filter((item): boolean =>
        item.policy.toLowerCase().includes(search.toLowerCase()),
      )
      .map(
        (item): IPolicyItem => ({
          element: (
            <IconButton
              disabled={!canMutate("remove_organization_priority_policy")}
              icon={"trash"}
              iconSize={"sm"}
              iconType={"fa-light"}
              id={"remove-priority"}
              onClick={handleDeletion(item)}
              variant={"ghost"}
            />
          ),
          id: item.policy.split("(")[0],
          policy: item.policy.split("(")[0],
          value: item.value,
        }),
      );
  }, [canMutate, criteria, handleDeletion, search]);

  const handleSearch = useCallback((value: string): void => {
    setSearch(value);
  }, []);

  const { table } = useTable({
    columns: [
      {
        accessorKey: "policy",
        header: t(`${prefix}modal.criterion`),
        size: 500,
      },
      {
        accessorKey: "value",
        header: t(`${prefix}modal.priorityValue`),
        size: 500,
      },
      {
        accessorKey: "element",
        cell: (cell): JSX.Element => cell.getValue() as unknown as JSX.Element,
        enableSorting: false,
        header: "",
      },
    ],
    data: tableData,
    id: "org-priority-policies",
    options: {
      actionsOptions: { onSearch: handleSearch },
      columnOptions: { sorting: [{ desc: true, id: "value" }] },
      filtersMode: "client-side",
      loading,
      paginationMode: "client-side",
    },
    total: tableData.length,
  });

  return (
    <Container mx={1.25} my={1.25}>
      <Fragment>
        {tableData.length === 0 && !loading ? (
          <EmptyState
            confirmButton={
              allowUpdate
                ? { onClick: open, text: t(`${prefix}empty.button`) }
                : undefined
            }
            description={t(`${prefix}empty.description`)}
            imageSrc={"integrates/empty/addRoot"}
            title={t(`${prefix}empty.title`)}
          />
        ) : (
          <Table table={table}>
            <Table.Options>
              <Table.Controls>
                <Container id={"actions-container"}>
                  <Button
                    disabled={!allowUpdate}
                    icon={"plus"}
                    id={"add-priority"}
                    onClick={open}
                  >
                    {t(`${prefix}button`)}
                  </Button>
                </Container>
              </Table.Controls>
            </Table.Options>
            <Table.Content />
            <Table.Footer />
          </Table>
        )}
        <ConfirmDialog />
        {isOpen ? (
          <AddCriteriaModal
            loading={loading}
            modalRef={modalProps}
            onSubmit={handleSubmit}
          />
        ) : undefined}
      </Fragment>
    </Container>
  );
};

export { VulnerabilitiesPriority };
